import { useEffect } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import { useFilters } from '@domain-group/react-hooks'

import { Button, DashboardModule } from '@domain-group/suit-theme'

import useAgencyProviderPackage from '@hooks/useAgencyProviderPackage'
import useAgencyProviderPackages from '@hooks/useAgencyProviderPackages'
import useReduxAction from '@hooks/useReduxAction'
import useSelectEntities from '@hooks/useSelectEntities'

import { confirmDelete } from '@functions/entity'

import DashboardModuleFilters from '@components/DashboardModuleFilters'

import { ModuleState } from '@redux/modules/types'

import AgencyProviderPackageListItem from './AgencyProviderPackageListItem'

const buildPageFilterFields = () => {
  const style = {
    marginBottom: 16,
  }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'agency_title',
      label: 'Filter by Title',
    },
  ]
}

interface AgencyProviderPackagesProps {
  providerPackageId: number,
}

const AgencyProviderPackages = (props: AgencyProviderPackagesProps) => {
  const { providerPackageId } = props

  const filterPayload = useFilters()
  const { pageFilters } = filterPayload

  const agencyProviderPackages = useAgencyProviderPackages({
    filters: { provider_package_id: providerPackageId, ...pageFilters },
    performHttpRequests: !!providerPackageId,
    requestOptions: { include_agency_provider_package_agency: true },
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredAgencyProviderPackages,
    filteredAgencyProviderPackagesCount,
    hasFilteredAgencyProviderPackages,
    loaded,
    loading,
  } = agencyProviderPackages

  useReduxAction(
    'providerPackages',
    'loadProviderPackage',
    { include_provider_package_agencies_count: true },
    [providerPackageId, filteredAgencyProviderPackagesCount],
    {
      dispatchAction: (action, requestOptions) => action(providerPackageId, requestOptions),
      // @ts-expect-error TS2322
      shouldPerformFn: ({ loading: loadingPackage }: ModuleState) => (
        providerPackageId && !loadingPackage && loaded
      ),
      skipLoadedForKeysCheck: true,
    },
  )

  const agencyPackagePayload = useAgencyProviderPackage({ provider_package_id: providerPackageId })
  const {
    callbacks: {
      addAgencyProviderPackages,
      bulkDeleteAgencyProviderPackages,
    },
    // @ts-expect-error TS2339
    providerPackage: { agency_provider_packages_count },
  } = agencyPackagePayload

  const resultsText = agency_provider_packages_count
    ? `Showing ${filteredAgencyProviderPackagesCount} of ${agency_provider_packages_count} Results`
    : ''

  const unselectEntitiesPayload = useSelectEntities()
  const {
    callbacks: {
      getSelectableListItemProps,
      deselectAll,
    },
    hasMinimumSelected,
    selectedEntitiesCount,
    selectedEntityIds,
  } = unselectEntitiesPayload

  const confirmDeleteParams = {
    callbacks: {
      afterAction: deselectAll,
      buildActionParams: () => (
        [{ agency_provider_package_ids: selectedEntityIds, provider_package_id: providerPackageId }]
      ),
      deleteFn: bulkDeleteAgencyProviderPackages,
    },
    options: { isBulkDelete: true },
    strings: {
      deleteString: 'unassign',
      entityName: 'Agencies',
    },
  }

  useEffect(() => {
    deselectAll()
  }, [filteredAgencyProviderPackagesCount])

  return (
    <DashboardModule
      contentBoxProps={{ flexDirection: 'column' }}
      headerRight={<DashboardModule.FilterHeader {...filterPayload} />}
      loading={loading}
      title="Agencies"
    >
      <DashboardModuleFilters
        filterFields={buildPageFilterFields()}
        {...filterPayload}
      />

      {/*
       // @ts-expect-error TS2322 */}
      <DashboardModule.ScrollableSection maxHeight={600}>
        {filteredAgencyProviderPackages.map((agencyProviderPackage) => {
          const { id } = agencyProviderPackage
          const listItemProps = getSelectableListItemProps(id)

          return (
            <AgencyProviderPackageListItem
              key={id}
              agencyProviderPackage={agencyProviderPackage}
              {...listItemProps}
            />
          )
        })}

        {!loading && !hasFilteredAgencyProviderPackages && (
          <DashboardModule.BlankState>
            <DashboardModule.BlankState.Title>
              No Assigned Agencies
            </DashboardModule.BlankState.Title>

            <DashboardModule.BlankState.Paragraph>
              Please adjust your filters or assign an agency
            </DashboardModule.BlankState.Paragraph>
          </DashboardModule.BlankState>
        )}
      </DashboardModule.ScrollableSection>

      <DashboardModule.LoadMoreFooter
        boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
        callbacks={{ loadMore }}
        canLoadMore={canLoadMore}
        entityCount={filteredAgencyProviderPackagesCount}
        loading={loading}
        // @ts-expect-error TS2741
        strings={{ resultsText }}
      >
        {hasMinimumSelected && (
          <Button
            buttonStyle="secondaryEdit"
            icon={<FontAwesomeIcon icon={faTrashAlt} />}
            marginRight={[0, 'medium']}
            marginTop={['medium', 0]}
            // @ts-expect-error TS2345
            onClick={() => confirmDelete(confirmDeleteParams)}
            // @ts-expect-error TS2322
            size={['medium', 'small']}
            width={['100%', 'fit-content']}
          >
            Unassign Selected Agencies ({selectedEntitiesCount})
          </Button>
        )}

        <Button
          buttonStyle="secondaryEdit"
          icon={<FontAwesomeIcon icon={faPlus} />}
          marginTop={['medium', 0]}
          onClick={() => addAgencyProviderPackages()}
          // @ts-expect-error TS2322
          size={['medium', 'small']}
          width={['100%', 'fit-content']}
        >
          Add Agencies
        </Button>
      </DashboardModule.LoadMoreFooter>
    </DashboardModule>
  )
}

export default AgencyProviderPackages
